import React from 'react';
import { Heading, IconSpinner, LayoutSingleColumn, NamedLink, Page } from '../../components';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import css from './CartPage.module.css';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import CartCard from './components/CartCard/CartCard';
import { useToggleCart } from '../../hooks';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import isEmpty from 'lodash/isEmpty';
import { useConfiguration } from '../../context/configurationContext';
import { handleSubmit } from '../ListingPage/ListingPage.shared';
import { useHistory } from 'react-router-dom';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { trackAddToCart } from '../../analytics/googleAnalyticsEcommerce';

const { Money, UUID } = sdkTypes;

/**
 * @typedef {Object} renderCartListingsProps
 * @param {Object} props
 * @param {Object[]} props.cartListings
 * @param {Object} props.cart
 * @param {Object} props.intl
 * @param {Object} props.updateCartWithSizeQuantitiesInProgressIds
 * @param {Object} props.onRemoveFromCart
 * @param {Object} props.onUpdateCartWithSizeQuantities
 * @param {Object} props.updateCartWithSizeQuantitiesError
 * @param {function} props.onCheckout
 * @returns {JSX.Element}
 * @description Renders the cart listings
 * @example
 * <CartPage />
 */
const renderCartListings = props => {
  const {
    cartListings,
    cart,
    intl,
    updateCartWithSizeQuantitiesInProgressIds,
    onRemoveFromCart,
    onUpdateCartWithSizeQuantities,
    updateCartWithSizeQuantitiesError,
    sizeEnumOptions,
    onCheckout,
  } = props;
  if (!cartListings.length) {
    return null;
  }

  const providerGroups = [];

  Object.entries(cart).forEach(([providerId, listingIdMap]) => {
    const listingIds = Object.keys(listingIdMap);
    if (!listingIds.length) {
      return;
    }

    // Collect all items from this provider
    const providerItems = [];
    let providerTotalItems = 0;
    let providerTotalPrice = 0;
    let providerCurrency;
    let firstAuthor = null;

    listingIds.forEach(listingId => {
      const listing = cartListings.find(l => l.id.uuid === listingId);
      if (!listing) return;

      if (!firstAuthor) {
        firstAuthor = listing?.author;
      }

      const listingCart = listingIdMap[listingId];
      const sizes = listingCart?.sizes || {};

      // Filter sizes with quantity > 0
      const validSizes = Object.entries(sizes).filter(([size, quantity]) => quantity > 0);

      if (validSizes.length > 0) {
        // Create an item for each size with quantity > 0
        validSizes.forEach(([size, quantity]) => {
          const sizeTotal = (listing?.attributes?.price?.amount ?? 0) * quantity;
          providerTotalItems += quantity;
          providerTotalPrice += sizeTotal;
          providerCurrency = listing?.attributes?.price?.currency;

          providerItems.push({
            listingId,
            listing,
            size,
            quantity,
            sizeTotal,
            sizeTotalFormatted: formatMoney(intl, new Money(sizeTotal, providerCurrency)),
            sizeLabel: sizeEnumOptions.find(option => option.option === size)?.label,
          });
        });
      }
    });

    if (providerItems.length > 0) {
      const inProgress = updateCartWithSizeQuantitiesInProgressIds.some(id =>
        providerItems.some(item => item.listingId === id)
      );
      const providerTotalFormatted = formatMoney(
        intl,
        new Money(providerTotalPrice, providerCurrency)
      );

      providerGroups.push({
        providerId,
        items: providerItems,
        inProgress,
        firstAuthor,
        providerTotalFormatted,
        providerTotalItems,
      });
    }
  });

  return providerGroups.map(group => (
    <CartCard
      key={group.providerId}
      providerId={group.providerId}
      items={group.items}
      inProgress={group.inProgress}
      updateCartWithSizeQuantitiesError={updateCartWithSizeQuantitiesError}
      onUpdateCartWithSizeQuantities={onUpdateCartWithSizeQuantities}
      onRemoveFromCart={onRemoveFromCart}
      firstAuthor={group.firstAuthor}
      providerTotalFormatted={group.providerTotalFormatted}
      providerTotalItems={group.providerTotalItems}
      onCheckout={() => onCheckout(group.providerId)}
    />
  ));
};

/**
 * @returns {JSX.Element}
 * @description CartPage component that displays the cart page
 * @example
 * <CartPage />
 */
const CartPage = () => {
  const intl = useIntl();
  const title = intl.formatMessage({ id: 'CartPage.title' });
  const ui = useSelector(state => state.ui);

  const scrollingDisabled = isScrollingDisabled({ ui });
  const cartListingIds = useSelector(state => state.CartPage.cartListingIds);
  const fetchCartListingsInProgress = useSelector(
    state => state.CartPage.fetchCartListingsInProgress
  );
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const marketplaceData = useSelector(state => state.marketplaceData);
  const cartListings = getListingsById({ marketplaceData }, cartListingIds);
  const cart = useSelector(state => state.cart.cart);
  const {
    updateCartWithSizeQuantities,
    updateCartWithSizeQuantitiesInProgressIds,
    updateCartWithSizeQuantitiesError,
  } = useToggleCart();

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities({ marketplaceData }, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const sizeEnumOptions =
    config?.listing?.listingFields?.find(field => field.key === 'size_id')?.enumOptions || [];

  const onRemoveFromCart = (providerId, listingId, size) => {
    const newSizes = { ...cart[providerId][listingId].sizes };
    delete newSizes[size];
    const totalQty = Object.values(newSizes).reduce((sum, q) => sum + Number(q), 0);
    updateCartWithSizeQuantities(providerId, listingId, newSizes).then(() => {
      if (totalQty <= 0) {
        return;
      }
      const listingEntity = getListing(new UUID(listingId));
      if (!listingEntity) {
        return;
      }
      trackAddToCart({
        listing: listingEntity,
        config,
        routeConfiguration,
        orderedSizeWithQuantities: newSizes,
        sizeEnumOptions,
      });
    });
  };

  const onUpdateCartWithSizeQuantities = (providerId, listingId, size, quantity) => {
    const newSizes = { ...cart[providerId][listingId].sizes, [size]: quantity };
    const totalQty = Object.values(newSizes).reduce((sum, q) => sum + Number(q), 0);
    updateCartWithSizeQuantities(providerId, listingId, newSizes).then(() => {
      if (totalQty <= 0) {
        return;
      }
      const listingEntity = getListing(new UUID(listingId));
      if (!listingEntity) {
        return;
      }
      trackAddToCart({
        listing: listingEntity,
        config,
        routeConfiguration,
        orderedSizeWithQuantities: newSizes,
        sizeEnumOptions,
      });
    });
  };

  const isCartEmpty = isEmpty(cart);

  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector(state => state.user.currentUser);
  const callSetInitialValues = (setInitialValues, values, saveToSessionStorage) => {
    dispatch(setInitialValues(values, saveToSessionStorage));
  };
  const onInitializeCardPaymentData = () => {
    dispatch(initializeCardPaymentData());
  };

  const onCheckout = providerId => {
    // Collect all items from this provider for checkout
    const providerCart = cart[providerId];

    const firstListingId = Object.keys(providerCart)[0];
    if (firstListingId) {
      handleSubmit({
        history,
        params: { id: firstListingId },
        currentUser,
        getListing,
        callSetInitialValues,
        onInitializeCardPaymentData,
        routes: routeConfiguration,
      })({
        orderedSizeWithQuantities: providerCart,
        fromCart: true,
      });
    }
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <TopbarContainer />
      <LayoutSingleColumn className={css.layout}>
        <div className={css.root}>
          <div className={css.titleWrapper}>
            <Heading as="h3" className={css.title}>
              {title}
            </Heading>
            {!isCartEmpty && (
              <NamedLink name="SearchPage" className={css.continueShoppingLink}>
                <FormattedMessage id="CartPage.continueShopping" />
              </NamedLink>
            )}
          </div>

          {fetchCartListingsInProgress ? (
            <IconSpinner />
          ) : !isCartEmpty ? (
            <div className={css.cartListings}>
              {renderCartListings({
                cartListings,
                cart,
                intl,
                updateCartWithSizeQuantitiesInProgressIds,
                updateCartWithSizeQuantitiesError,
                onRemoveFromCart,
                onUpdateCartWithSizeQuantities,
                sizeEnumOptions,
                onCheckout,
              })}
            </div>
          ) : (
            <div className={css.emptyCart}>
              <Heading as="h3" className={css.emptyCartTitle}>
                <FormattedMessage id="CartPage.emptyCart" />
              </Heading>
              <NamedLink name="SearchPage" className={css.goShoppingLink}>
                <FormattedMessage id="CartPage.goShopping" />
              </NamedLink>
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CartPage;

CartPage.displayName = 'CartPage';
